<script setup lang="ts">
import { Float } from "@headlessui-float/vue";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { EllipsisVerticalIcon, PencilSquareIcon } from "@heroicons/vue/20/solid";

import HasPermission from "@/components/HasPermission.vue";

import useDateFormat from "@/composables/useDateFormat";
import useHasPermissionUser from "@/composables/useHasPermissionUser";

import { Brand } from "@/internal/modules/providers/interfaces/Brand.ts";

interface Props {
  data: Brand[];
  isLoading?: boolean;
}

interface Emits {
  (e: "actionModal"): void;
  (e: "restore", id: number): Promise<void>;
  (e: "delete", id: number): Promise<void>;
}

const emits = defineEmits<Emits>();
defineProps<Props>();

const { hasPermissionUser } = useHasPermissionUser();
</script>

<template>
  <div class="w-full">
    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="flex h-80 items-center justify-center rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50"
    >
      <div class="flex flex-col items-center gap-4">
        <div class="relative">
          <div class="h-12 w-12 rounded-full border-4 border-purple-100"></div>
          <div class="absolute inset-0 h-12 w-12 animate-spin rounded-full border-4 border-transparent border-t-purple-500"></div>
        </div>
        <span class="text-sm font-medium text-gray-500">Cargando marcas...</span>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!data?.length"
      class="flex h-80 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gradient-to-br from-gray-50 to-white"
    >
      <div class="flex h-20 w-20 items-center justify-center rounded-2xl bg-purple-100">
        <span class="material-symbols-outlined text-4xl text-purple-600">branding_watermark</span>
      </div>
      <h3 class="mt-5 text-lg font-semibold text-gray-900">No hay marcas</h3>
      <p class="mt-1 text-sm text-gray-500">Comienza creando tu primera marca</p>
      <HasPermission :name-permission="'create-internal'" :entity-type="'App\\Models\\Brand'">
        <button
          @click="emits('actionModal')"
          class="mt-6 inline-flex items-center gap-2 rounded-xl bg-purple-600 px-5 py-2.5 text-sm font-medium text-white shadow-lg shadow-purple-500/25 transition-all hover:bg-purple-700 hover:shadow-xl hover:shadow-purple-500/30"
        >
          <span class="material-symbols-outlined text-lg">add</span>
          Crear marca
        </button>
      </HasPermission>
    </div>

    <!-- Mobile Cards (visible on small screens) -->
    <div v-else class="block lg:hidden space-y-3">
      <div
        v-for="entry in data"
        :key="entry.id"
        class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <!-- Header -->
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-3">
            <div
              v-if="entry.logo"
              class="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white"
            >
              <img :src="entry.logo" :alt="`Logo de ${entry.name}`" class="h-full w-full object-contain" />
            </div>
            <div
              v-else
              class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-md"
            >
              <span class="material-symbols-outlined text-xl text-white">branding_watermark</span>
            </div>
            <div>
              <p class="font-semibold text-gray-900">{{ entry.name }}</p>
              <p class="text-xs text-gray-500">{{ entry.chainName ?? "Sin cadena" }}</p>
            </div>
          </div>
          <span
            :class="[
              'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium',
              entry.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600',
            ]"
          >
            <span :class="['h-1.5 w-1.5 rounded-full', entry.isActive ? 'bg-emerald-500' : 'bg-gray-400']"></span>
            {{ entry.isActive ? "Activo" : "Inactivo" }}
          </span>
        </div>

        <!-- Info -->
        <div class="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
          <div class="flex items-center gap-4 text-sm text-gray-500">
            <div class="flex items-center gap-1">
              <span class="material-symbols-outlined text-base">apartment</span>
              {{ entry.hotelsCount }} hoteles
            </div>
            <div class="flex items-center gap-1">
              <span class="material-symbols-outlined text-base">calendar_today</span>
              {{ useDateFormat(entry.createdAt).date }}
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div
          v-if="
            hasPermissionUser('viewAny-internal', 'App\\Models\\Brand') ||
            hasPermissionUser('delete-internal', 'App\\Models\\Brand') ||
            hasPermissionUser('restore-internal', 'App\\Models\\Brand')
          "
          class="mt-3 flex gap-2"
        >
          <RouterLink
            v-if="entry.isActive && hasPermissionUser('viewAny-internal', 'App\\Models\\Brand')"
            :to="{ name: 'hotel.brands.edit', params: { id: entry.id } }"
            class="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            <PencilSquareIcon class="h-4 w-4" />
            Editar
          </RouterLink>
          <button
            v-if="entry.isActive && hasPermissionUser('delete-internal', 'App\\Models\\Brand')"
            @click="emits('delete', entry.id)"
            class="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
          >
            <span class="material-symbols-outlined text-lg">block</span>
            Desactivar
          </button>
          <button
            v-if="!entry.isActive && hasPermissionUser('restore-internal', 'App\\Models\\Brand')"
            @click="emits('restore', entry.id)"
            class="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-600 transition-colors hover:bg-emerald-100"
          >
            <span class="material-symbols-outlined text-lg">check_circle</span>
            Activar
          </button>
        </div>
      </div>
    </div>

    <!-- Desktop Table (hidden on small screens) -->
    <div v-if="data?.length" class="hidden lg:block overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead>
            <tr class="bg-gradient-to-r from-gray-50 to-gray-100/50">
              <th scope="col" class="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Marca
              </th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Cadena
              </th>
              <th scope="col" class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                Hoteles
              </th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Fecha de creacion
              </th>
              <th scope="col" class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                Estatus
              </th>
              <th
                v-if="
                  hasPermissionUser('viewAny-internal', 'App\\Models\\Brand') ||
                  hasPermissionUser('delete-internal', 'App\\Models\\Brand') ||
                  hasPermissionUser('restore-internal', 'App\\Models\\Brand')
                "
                scope="col"
                class="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-600"
              >
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="entry in data" :key="entry.id" class="group transition-colors hover:bg-purple-50/50">
              <!-- Brand with logo -->
              <td class="whitespace-nowrap px-6 py-4">
                <div class="flex items-center gap-4">
                  <div
                    v-if="entry.logo"
                    class="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white"
                  >
                    <img :src="entry.logo" :alt="`Logo de ${entry.name}`" class="h-full w-full object-contain" />
                  </div>
                  <div
                    v-else
                    class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-md shadow-purple-500/20"
                  >
                    <span class="material-symbols-outlined text-xl text-white">branding_watermark</span>
                  </div>
                  <p class="font-semibold text-gray-900">{{ entry.name }}</p>
                </div>
              </td>

              <!-- Chain -->
              <td class="whitespace-nowrap px-6 py-4">
                <span class="text-sm text-gray-600">{{ entry.chainName ?? "Sin cadena" }}</span>
              </td>

              <!-- Hotels count -->
              <td class="whitespace-nowrap px-6 py-4 text-center">
                <RouterLink
                  :to="{
                    name: 'hotel.brands.edit',
                    params: { id: entry.id },
                    query: { tab: 'Listado de hoteles' },
                  }"
                  :class="[
                    'inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-all',
                    hasPermissionUser('viewAny-internal', 'App\\Models\\Brand') && entry.isActive
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 hover:shadow-md'
                      : 'cursor-not-allowed bg-gray-100 text-gray-400 pointer-events-none',
                  ]"
                >
                  <span class="material-symbols-outlined text-lg">apartment</span>
                  {{ entry.hotelsCount }}
                </RouterLink>
              </td>

              <!-- Created date -->
              <td class="whitespace-nowrap px-6 py-4">
                <div class="flex items-center gap-2 text-sm text-gray-500">
                  <span class="material-symbols-outlined text-base text-gray-400">calendar_today</span>
                  {{ useDateFormat(entry.createdAt).date }}
                </div>
              </td>

              <!-- Status -->
              <td class="whitespace-nowrap px-6 py-4 text-center">
                <span
                  :class="[
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold',
                    entry.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600',
                  ]"
                >
                  <span
                    :class="['h-2 w-2 rounded-full', entry.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400']"
                  ></span>
                  {{ entry.isActive ? "Activo" : "Inactivo" }}
                </span>
              </td>

              <!-- Actions -->
              <td
                v-if="
                  hasPermissionUser('viewAny-internal', 'App\\Models\\Brand') ||
                  hasPermissionUser('delete-internal', 'App\\Models\\Brand') ||
                  hasPermissionUser('restore-internal', 'App\\Models\\Brand')
                "
                class="whitespace-nowrap px-6 py-4"
              >
                <div class="flex items-center justify-center gap-2">
                  <RouterLink
                    v-if="entry.isActive && hasPermissionUser('viewAny-internal', 'App\\Models\\Brand')"
                    :to="{ name: 'hotel.brands.edit', params: { id: entry.id } }"
                    class="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 transition-all hover:bg-gray-200"
                  >
                    <PencilSquareIcon class="h-4 w-4" />
                    Editar
                  </RouterLink>

                  <Menu as="div" class="relative">
                    <Float
                      strategy="fixed"
                      placement="bottom-end"
                      :offset="4"
                      enter="transition ease-out duration-100"
                      enter-from="transform opacity-0 scale-95"
                      enter-to="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leave-from="transform opacity-100 scale-100"
                      leave-to="transform opacity-0 scale-95"
                    >
                      <MenuButton
                        class="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                      >
                        <EllipsisVerticalIcon class="h-5 w-5" aria-hidden="true" />
                      </MenuButton>

                      <MenuItems
                        class="w-44 overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5 focus:outline-none"
                      >
                        <div class="py-1">
                          <MenuItem
                            v-slot="{ active }"
                            v-if="entry.isActive && hasPermissionUser('delete-internal', 'App\\Models\\Brand')"
                          >
                            <button
                              @click="emits('delete', entry.id)"
                              :class="[
                                active ? 'bg-red-50' : '',
                                'flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red-600',
                              ]"
                            >
                              <span class="material-symbols-outlined text-lg">block</span>
                              Desactivar
                            </button>
                          </MenuItem>

                          <MenuItem
                            v-slot="{ active }"
                            v-if="!entry.isActive && hasPermissionUser('restore-internal', 'App\\Models\\Brand')"
                          >
                            <button
                              @click="emits('restore', entry.id)"
                              :class="[
                                active ? 'bg-emerald-50' : '',
                                'flex w-full items-center gap-2 px-4 py-2.5 text-sm text-emerald-600',
                              ]"
                            >
                              <span class="material-symbols-outlined text-lg">check_circle</span>
                              Activar
                            </button>
                          </MenuItem>

                          <MenuItem
                            v-if="!entry.isActive && !hasPermissionUser('restore-internal', 'App\\Models\\Brand')"
                          >
                            <p class="px-4 py-2.5 text-sm text-gray-400">Sin opciones</p>
                          </MenuItem>
                        </div>
                      </MenuItems>
                    </Float>
                  </Menu>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination slot -->
    <slot name="pagination" />
  </div>
</template>
